<template>
  <div id="billing" class="profile-billing">
    <div v-if="verifying" class="state-loading mb-30" role="status" aria-live="polite" aria-busy="true">
      {{ $t('billing.verifying_payment') }}
    </div>
    <div v-else-if="alertMessage" class="alert mb-30" :class="alertClass" role="alert">
      {{ alertMessage }}
    </div>

    <div v-if="loading" class="state-loading mb-30" role="status" aria-live="polite" aria-busy="true">
      {{ $t('billing.loading_plans') }}
    </div>

    <template v-else>
      <section
        class="billing-plan-card"
        :class="currentPlanCardClass"
        aria-labelledby="billing-current-heading"
      >
        <div class="billing-plan-card__inner">
          <header class="billing-plan-card__header">
            <div class="billing-plan-card__identity">
              <div class="billing-plan-card__intro">
                <p class="billing-plan-card__eyebrow">{{ $t('billing.current_plan') }}</p>
                <div class="billing-plan-card__title-row">
                  <h2 id="billing-current-heading" class="billing-plan-card__name">{{ currentPlanName }}</h2>
                  <span class="billing-status" :class="statusToneClass">{{ currentStatusLabel }}</span>
                </div>
                <p class="billing-plan-card__price">{{ currentPlanPriceLabel }}</p>
              </div>
            </div>
            <button
              v-if="canCancelCurrent"
              type="button"
              class="billing-plan-card__cancel"
              :disabled="cancelling"
              @click="onCancel"
            >
              <i :class="[cancelling ? 'fas fa-spinner fa-spin' : 'fas fa-times']" aria-hidden="true"></i>
              <span>{{ $t('billing.cancel_subscription') }}</span>
            </button>
          </header>

          <div class="billing-plan-card__stats">
            <div
              v-if="subscription?.current_period_end && daysRemaining !== null"
              class="billing-plan-card__stat billing-plan-card__stat--highlight"
            >
              <span class="billing-plan-card__stat-label">{{ periodEndLabel || $t('billing.plan_term') }}</span>
              <span class="billing-plan-card__stat-value billing-plan-card__stat-value--lg">
                {{ periodRemainingLabel }}
              </span>
            </div>
            <div v-else class="billing-plan-card__stat">
              <span class="billing-plan-card__stat-label">{{ $t('billing.plan_term') }}</span>
              <span class="billing-plan-card__stat-value">{{ $t('billing.free_forever') }}</span>
            </div>
            <div v-if="currentPlanProviderLabel" class="billing-plan-card__stat">
              <span class="billing-plan-card__stat-label">{{ $t('global.provider') }}</span>
              <span class="billing-plan-card__stat-value">{{ currentPlanProviderLabel }}</span>
            </div>
          </div>

          <div
            v-if="billingPeriodPercent !== null"
            class="billing-plan-card__period"
            role="group"
            :aria-label="$t('billing.billing_period_progress')"
          >
            <div class="billing-plan-card__period-track" aria-hidden="true">
              <span class="billing-plan-card__period-fill" :style="{ width: `${billingPeriodPercent}%` }"></span>
            </div>
            <p v-if="periodRemainingLabel" class="billing-plan-card__period-caption">
              {{ periodRemainingLabel }}
            </p>
          </div>

          <div
            v-if="showAIUsage"
            class="billing-plan-card__usage"
            role="group"
            :aria-label="aiTokenLabel"
          >
            <div class="billing-plan-card__usage-head">
              <span class="billing-plan-card__usage-title">{{ $t('billing.limit_ai') }}</span>
              <span class="billing-plan-card__usage-nums">{{ aiTokenLabel }}</span>
            </div>
            <div v-if="aiUsagePercent !== null" class="billing-plan-card__usage-track" aria-hidden="true">
              <span class="billing-plan-card__usage-fill" :style="{ width: `${aiUsagePercent}%` }"></span>
            </div>
          </div>
        </div>
      </section>

      <section class="billing-compare" aria-labelledby="billing-compare-heading">
        <h3 id="billing-compare-heading" class="billing-compare__title">{{ $t('billing.change_plan') }}</h3>

        <div v-if="!plans.length" class="state-error" role="alert">
          <div class="state-error__title">{{ $t('billing.no_plans') }}</div>
          <button type="button" class="btn btn-sm btn-outline-danger" @click="retryLoad">{{ $t('common.retry') }}</button>
        </div>

        <div v-else class="billing-plans">
          <article
            v-for="plan in plans"
            :key="plan.slug"
            :class="['billing-plan', { 'billing-plan--current': isCurrentPlan(plan) }]"
          >
            <div class="billing-plan__top">
              <h4 class="billing-plan__name">{{ plan.name }}</h4>
              <span v-if="isCurrentPlan(plan)" class="billing-plan__badge">{{ $t('billing.current_plan') }}</span>
            </div>
            <div class="billing-plan__price-row">
              <span class="billing-plan__price">{{ formatPrimaryPrice(plan) }}</span>
              <span v-if="planPrimaryAmount(plan) > 0" class="billing-plan__interval">
                {{ $t('billing.per_interval', { interval: plan.interval }) }}
              </span>
              <span v-if="secondaryPrice(plan)" class="billing-plan__alt">
                {{ $t('billing.or_per_month', { price: secondaryPrice(plan) }) }}
              </span>
            </div>
            <ul class="billing-plan__features">
              <li v-for="(label, idx) in planFeatureList(plan)" :key="idx">
                <i class="fas fa-check" aria-hidden="true"></i>{{ label }}
              </li>
            </ul>
            <button
              type="button"
              class="btn billing-plan__action"
              :class="planActionClass(plan)"
              :disabled="isSubscribeDisabled(plan)"
              @click="onSubscribe(plan)"
            >
              {{ planButtonLabel(plan) }}
            </button>
          </article>
        </div>

        <p v-if="!paymentReady" class="billing-compare__soon">{{ $t('billing.paid_plans_soon') }}</p>
        <p class="billing-compare__legal">
          <router-link to="/refund">{{ $t('legal.refund') }}</router-link>
        </p>
      </section>

      <ContentCard>
        <div class="billing-payments__head">
          <h3 class="billing-payments__title">{{ $t('profile.transactions_title') }}</h3>
          <button type="button" class="btn btn-outline-secondary billing-payments__refresh" @click="loadTransactions">
            <i class="fas fa-sync-alt mr-1"></i>{{ $t('common.refresh') }}
          </button>
        </div>

        <div v-if="transactionsLoading" class="state-loading" role="status" aria-live="polite" aria-busy="true">
          {{ $t('billing.loading_payments') }}
        </div>
        <div v-else-if="paymentsError" class="state-error" role="alert">
          <div class="state-error__title">{{ $t('common.load_failed') }}</div>
          <button type="button" class="btn btn-sm btn-outline-danger" @click="loadTransactions">{{ $t('common.retry') }}</button>
        </div>
        <template v-else>
          <div class="billing-tx-toolbar">
            <div class="billing-tx-filters" role="toolbar" :aria-label="$t('billing.tx_filter_label')">
              <button
                v-for="filter in transactionFilters"
                :key="filter.value"
                type="button"
                class="btn btn-sm"
                :class="transactionsCategory === filter.value ? 'btn-primary' : 'btn-outline-secondary'"
                @click="setTransactionCategory(filter.value)"
              >
                {{ filter.label }}
              </button>
            </div>
            <div class="billing-tx-filters billing-tx-filters--secondary">
              <select
                v-model="transactionsPlan"
                class="form-control form-control-sm billing-tx-select"
                :aria-label="$t('global.plan')"
                @change="onTransactionFiltersChange"
              >
                <option value="">{{ $t('billing.tx_all_plans') }}</option>
                <option v-for="plan in plans" :key="plan.slug" :value="plan.slug">{{ plan.name }}</option>
              </select>
              <select
                v-model="transactionsProvider"
                class="form-control form-control-sm billing-tx-select"
                :aria-label="$t('global.provider')"
                @change="onTransactionFiltersChange"
              >
                <option value="">{{ $t('billing.tx_all_providers') }}</option>
                <option value="paypal">{{ $t('billing.provider_paypal') }}</option>
                <option value="pakasir">{{ $t('billing.provider_local') }}</option>
              </select>
            </div>
          </div>

          <p v-if="!subscriptions.length" class="billing-payments__empty">
            {{ hasTransactionFilters ? $t('billing.no_payments_filtered') : $t('billing.no_payments') }}
          </p>
          <div v-else class="table-responsive billing-tx-table-wrap">
            <table class="table table-hover billing-tx-table mb-0">
              <thead>
                <tr>
                  <th scope="col">{{ $t('global.date') }}</th>
                  <th scope="col">{{ $t('global.plan') }}</th>
                  <th scope="col">{{ $t('billing.tx_billing_type') }}</th>
                  <th scope="col">{{ $t('global.provider') }}</th>
                  <th scope="col">{{ $t('global.period') }}</th>
                  <th scope="col">{{ $t('global.status') }}</th>
                  <th scope="col" class="billing-tx-table__actions">{{ $t('global.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="sub in subscriptions" :key="sub.id">
                  <td class="billing-tx-table__date">{{ formatDateShort(sub.created_at) }}</td>
                  <td>
                    <span class="billing-tx-table__plan">{{ sub.plan?.name || $t('billing.free_price') }}</span>
                  </td>
                  <td>
                    <span class="billing-tx-type" :class="billingTypeClass(sub)">{{ billingTypeLabel(sub) }}</span>
                  </td>
                  <td>{{ providerLabel(sub.payment_provider) }}</td>
                  <td class="billing-tx-table__period">
                    <template v-if="sub.current_period_end">
                      {{ formatDateShort(sub.current_period_start) }}–{{ formatDateShort(sub.current_period_end) }}
                    </template>
                    <span v-else class="billing-tx-table__dash">—</span>
                  </td>
                  <td>
                    <span class="billing-status" :class="statusTone(sub.status)">{{ statusLabel(sub.status) }}</span>
                  </td>
                  <td class="billing-tx-table__actions">
                    <button
                      type="button"
                      class="btn btn-outline-secondary btn-sm"
                      @click="openTransactionDetail(sub)"
                    >
                      {{ $t('billing.tx_detail') }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="subscriptionsTotal > 0" class="billing-tx-footer">
            <p class="billing-tx-footer__meta">{{ transactionsRangeLabel }}</p>
            <div class="billing-tx-footer__controls">
              <label class="billing-tx-footer__limit">
                <span class="sr-only">{{ $t('common.rows_per_page') }}</span>
                <select
                  v-model.number="transactionsLimit"
                  class="form-control form-control-sm"
                  @change="onTransactionsLimitChange"
                >
                  <option v-for="n in transactionsLimitOptions" :key="n" :value="n">{{ n }}</option>
                </select>
              </label>
              <nav class="billing-tx-pagination" :aria-label="$t('billing.transactions_pagination')">
                <ul class="pagination pagination-sm mb-0">
                  <li class="page-item" :class="{ disabled: transactionsPage <= 1 }">
                    <button
                      type="button"
                      class="page-link"
                      :disabled="transactionsPage <= 1"
                      @click="onTransactionsPageChange(transactionsPage - 1)"
                    >
                      {{ $t('common.page_prev') }}
                    </button>
                  </li>
                  <li
                    v-for="p in visibleTransactionPages"
                    :key="p"
                    class="page-item"
                    :class="{ active: p === transactionsPage }"
                  >
                    <button type="button" class="page-link" @click="onTransactionsPageChange(p)">{{ p }}</button>
                  </li>
                  <li class="page-item" :class="{ disabled: transactionsPage >= transactionsPageCount }">
                    <button
                      type="button"
                      class="page-link"
                      :disabled="transactionsPage >= transactionsPageCount"
                      @click="onTransactionsPageChange(transactionsPage + 1)"
                    >
                      {{ $t('common.page_next') }}
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </template>
      </ContentCard>
    </template>

    <modal
      name="profile-payment-modal"
      height="auto"
      :adaptive="true"
      width="640"
      :scrollable="true"
      :click-to-close="!subscribing"
      :focus-trap="true"
      @opened="onPaymentOpened"
      @closed="onPaymentClosed"
    >
      <div
        class="payment-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="payment-modal-title"
      >
        <div class="payment-modal__head">
          <h4 id="payment-modal-title" class="payment-modal__title">{{ $t('billing.payment_method') }}</h4>
          <button
            type="button"
            class="payment-modal__close"
            :disabled="subscribing"
            :aria-label="$t('billing.close_modal')"
            @click="closePaymentModal"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div v-if="selectedPlan" class="payment-modal__plan">
          <span class="payment-modal__plan-name">{{ selectedPlan.name }}</span>
          <span class="payment-modal__plan-price">
            {{ formatPrimaryPrice(selectedPlan) }}
            <template v-if="secondaryPrice(selectedPlan)"> · {{ secondaryPrice(selectedPlan) }}</template>
          </span>
        </div>

        <div class="payment-modal__options">
          <button
            v-if="paypalReady"
            type="button"
            class="payment-option payment-option--paypal"
            :disabled="subscribing"
            @click="confirmSubscribe('paypal')"
          >
            <span class="payment-option__icon"><i class="fab fa-paypal"></i></span>
            <span class="payment-option__body">
              <strong class="payment-option__title">PayPal</strong>
            </span>
          </button>

          <button
            v-if="pakasirReady"
            type="button"
            class="payment-option payment-option--pakasir"
            :disabled="subscribing"
            @click="confirmSubscribe('pakasir')"
          >
            <span class="payment-option__icon"><i class="fas fa-qrcode"></i></span>
            <span class="payment-option__body">
              <strong class="payment-option__title">{{ $t('billing.provider_local') }}</strong>
            </span>
          </button>
        </div>

        <p class="payment-modal__notice">
          <router-link to="/refund">{{ $t('legal.refund') }}</router-link>
        </p>
      </div>
    </modal>

    <modal
      name="billing-tx-detail-modal"
      height="auto"
      :adaptive="true"
      width="560"
      :scrollable="true"
      :click-to-close="!txDetailLoading"
      :focus-trap="true"
      @closed="onTxDetailClosed"
    >
      <div
        v-if="txDetail"
        class="billing-tx-detail"
        role="dialog"
        aria-modal="true"
        aria-labelledby="billing-tx-detail-title"
      >
        <div class="billing-tx-detail__head">
          <h4 id="billing-tx-detail-title" class="billing-tx-detail__title">{{ $t('billing.tx_detail_title') }}</h4>
          <button
            type="button"
            class="billing-tx-detail__close"
            :disabled="txDetailLoading"
            :aria-label="$t('billing.close_modal')"
            @click="closeTransactionDetail"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div v-if="txDetailLoading" class="state-loading" role="status" aria-live="polite" aria-busy="true">
          {{ $t('common.loading') }}
        </div>
        <template v-else>
          <dl class="billing-tx-detail__meta">
            <div class="billing-tx-detail__row">
              <dt>{{ $t('global.plan') }}</dt>
              <dd>{{ txDetail.plan?.name || $t('billing.free_price') }}</dd>
            </div>
            <div class="billing-tx-detail__row">
              <dt>{{ $t('global.status') }}</dt>
              <dd><span class="billing-status" :class="statusTone(txDetail.status)">{{ statusLabel(txDetail.status) }}</span></dd>
            </div>
            <div class="billing-tx-detail__row">
              <dt>{{ $t('billing.tx_billing_type') }}</dt>
              <dd><span class="billing-tx-type" :class="billingTypeClass(txDetail)">{{ billingTypeLabel(txDetail) }}</span></dd>
            </div>
            <div class="billing-tx-detail__row">
              <dt>{{ $t('global.provider') }}</dt>
              <dd>{{ providerLabel(txDetail.payment_provider) }}</dd>
            </div>
            <div v-if="txDetailAmount" class="billing-tx-detail__row">
              <dt>{{ $t('global.amount') }}</dt>
              <dd>{{ txDetailAmount }}</dd>
            </div>
            <div v-if="txDetailOrderRef" class="billing-tx-detail__row">
              <dt>{{ $t('billing.tx_reference') }}</dt>
              <dd><code class="billing-tx-detail__code">{{ txDetailOrderRef }}</code></dd>
            </div>
            <div class="billing-tx-detail__row">
              <dt>{{ $t('global.date') }}</dt>
              <dd>{{ formatDate(txDetail.created_at) }}</dd>
            </div>
            <div v-if="txDetail.current_period_end" class="billing-tx-detail__row">
              <dt>{{ $t('global.period') }}</dt>
              <dd>{{ formatDateShort(txDetail.current_period_start) }}–{{ formatDateShort(txDetail.current_period_end) }}</dd>
            </div>
          </dl>

          <div v-if="txDetailFeatures.length" class="billing-tx-detail__features">
            <h5 class="billing-tx-detail__features-title">{{ $t('admin.features') }}</h5>
            <ul>
              <li v-for="(label, idx) in txDetailFeatures" :key="idx">{{ label }}</li>
            </ul>
          </div>

          <div class="billing-tx-detail__footer">
            <button
              v-if="isPayable(txDetail)"
              type="button"
              class="btn btn-primary btn-sm"
              :disabled="payingId === txDetail.id"
              @click="onPay(txDetail)"
            >
              <i v-if="payingId === txDetail.id" class="fas fa-spinner fa-spin mr-1" aria-hidden="true"></i>
              {{ $t('billing.pay_now') }}
            </button>
            <button
              v-if="canDownloadInvoice(txDetail)"
              type="button"
              class="btn btn-outline-primary btn-sm"
              :disabled="invoiceDownloadingId === txDetail.id"
              @click="onDownloadInvoice(txDetail)"
            >
              <i v-if="invoiceDownloadingId === txDetail.id" class="fas fa-spinner fa-spin mr-1" aria-hidden="true"></i>
              {{ $t('billing.tx_download_invoice') }}
            </button>
            <button type="button" class="btn btn-light btn-sm" @click="closeTransactionDetail">{{ $t('common.close') }}</button>
          </div>
        </template>
      </div>
    </modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import {
  formatCurrency,
  planPrimaryAmount,
  planSecondaryAmount,
  planSecondaryCurrency,
} from '@/utils/currency'
import { planFeatureList as buildPlanFeatureList } from '@/utils/planFeatures'

export default {
  name: 'ProfileBilling',
  data() {
    return {
      subscribing: false,
      cancelling: false,
      payingId: null,
      transactionsPage: 1,
      transactionsLimit: 5,
      transactionsLimitOptions: [5, 10, 20, 50],
      transactionsCategory: '',
      transactionsProvider: '',
      transactionsPlan: '',
      transactionsLoading: false,
      paymentsError: false,
      verifying: false,
      alertMessage: '',
      alertClass: 'alert-info',
      selectedPlan: null,
      txDetail: null,
      txDetailLoading: false,
      invoiceDownloadingId: null,
    }
  },
  computed: {
    ...mapGetters('billing', [
      'plans',
      'subscription',
      'subscriptions',
      'subscriptionsTotal',
      'loading',
      'currentPlanSlug',
      'paypalReady',
      'pakasirReady',
      'paymentReady',
      'aiTokens',
    ]),
    ...mapGetters('app', ['locale', 'currency', 'hideAI', 'hideScheduler']),

    showAIUsage() {
      if (this.hideAI) return false
      const usage = this.aiTokens
      if (!usage) return false
      return usage.limit !== 0 || Number(usage.used) > 0
    },
    aiTokenLabel() {
      if (this.hideAI) return ''
      const usage = this.aiTokens
      if (!usage || (usage.limit === 0 && !usage.used)) return ''
      if (usage.limit < 0) {
        return this.$t('billing.ai_tokens_used_unlimited', { used: this.formatCount(usage.used || 0) })
      }
      return this.$t('billing.ai_tokens_used', {
        used: this.formatCount(usage.used || 0),
        limit: this.formatCount(usage.limit),
      })
    },
    aiUsagePercent() {
      if (this.hideAI) return null
      const usage = this.aiTokens
      if (!usage || usage.limit <= 0) return null
      const used = Number(usage.used) || 0
      const limit = Number(usage.limit) || 1
      return Math.min(100, Math.round((used / limit) * 100))
    },
    currentPlanName() {
      return this.subscription?.plan?.name || this.$t('billing.free_price')
    },
    currentStatusLabel() {
      return this.statusLabel(this.subscription?.status || 'active')
    },
    statusToneClass() {
      return this.statusTone(this.subscription?.status || 'active')
    },
    periodEndLabel() {
      if (!this.subscription?.current_period_end) return ''
      if (this.subscription.status === 'cancelled') return this.$t('billing.ends_on')
      return this.$t('billing.period_end')
    },
    daysRemaining() {
      if (!this.subscription?.current_period_end) return null
      const end = new Date(this.subscription.current_period_end)
      const diff = Math.ceil((end - Date.now()) / (1000 * 60 * 60 * 24))
      return diff > 0 ? diff : 0
    },
    periodRemainingLabel() {
      if (!this.subscription?.current_period_end || this.daysRemaining === null) return ''
      const count = this.daysRemaining
      if (count === 0) return this.$t('billing.period_ends_today')
      if (count === 1) return this.$t('billing.days_remaining_one')
      return this.$t('billing.days_remaining', { count })
    },
    canCancelCurrent() {
      return this.subscription?.status === 'active' && this.subscription.plan?.slug !== 'free'
    },
    currentPlanDetail() {
      return this.subscription?.plan || this.plans.find((p) => p.slug === this.currentPlanSlug) || null
    },
    currentPlanPriceLabel() {
      const plan = this.currentPlanDetail
      if (!plan) return this.$t('billing.free_price')
      const amount = this.planPrimaryAmount(plan)
      if (!amount) return this.$t('billing.free_price')
      const price = this.formatPrimaryPrice(plan)
      if (plan.interval) {
        return this.$t('billing.per_period', { count: price, interval: plan.interval })
      }
      return price
    },
    currentPlanCardClass() {
      const slug = this.currentPlanSlug || 'free'
      return `billing-plan-card--${slug}`
    },
    currentPlanProviderLabel() {
      const provider = this.subscription?.payment_provider
      if (!provider || this.currentPlanSlug === 'free') return ''
      return this.providerLabel(provider)
    },
    billingPeriodPercent() {
      const sub = this.subscription
      if (!sub?.current_period_start || !sub?.current_period_end) return null
      const start = new Date(sub.current_period_start).getTime()
      const end = new Date(sub.current_period_end).getTime()
      if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null
      const elapsed = Date.now() - start
      return Math.min(100, Math.max(0, Math.round((elapsed / (end - start)) * 100)))
    },
    transactionsPageCount() {
      return Math.max(1, Math.ceil(this.subscriptionsTotal / this.transactionsLimit))
    },
    visibleTransactionPages() {
      const total = this.transactionsPageCount
      const current = this.transactionsPage
      const window = 5
      let start = Math.max(1, current - Math.floor(window / 2))
      let end = Math.min(total, start + window - 1)
      start = Math.max(1, end - window + 1)
      const pages = []
      for (let i = start; i <= end; i += 1) pages.push(i)
      return pages
    },
    transactionsRangeLabel() {
      if (!this.subscriptionsTotal) return ''
      const from = (this.transactionsPage - 1) * this.transactionsLimit + 1
      const to = Math.min(this.transactionsPage * this.transactionsLimit, this.subscriptionsTotal)
      return this.$t('common.showing_range', {
        from: this.formatCount(from),
        to: this.formatCount(to),
        total: this.formatCount(this.subscriptionsTotal),
      })
    },
    transactionFilters() {
      return [
        { label: this.$t('common.all'), value: '' },
        { label: this.$t('billing.tx_filter_unpaid'), value: 'unpaid' },
        { label: this.$t('billing.tx_filter_active'), value: 'active' },
        { label: this.$t('billing.tx_filter_recurring'), value: 'recurring' },
        { label: this.$t('billing.tx_filter_ended'), value: 'ended' },
      ]
    },
    hasTransactionFilters() {
      return !!(this.transactionsCategory || this.transactionsProvider || this.transactionsPlan)
    },
    planFeatureFlags() {
      return { hide_ai: this.hideAI, hide_scheduler: this.hideScheduler }
    },
    txDetailFeatures() {
      if (!this.txDetail?.plan) return []
      return buildPlanFeatureList(this.txDetail.plan, this.$t.bind(this), this.planFeatureFlags)
    },
    txDetailOrderRef() {
      if (!this.txDetail) return ''
      return this.txDetail.paypal_subscription_id || this.txDetail.pakasir_order_id || ''
    },
    txDetailAmount() {
      const plan = this.txDetail?.plan
      if (!plan || !this.txDetail?.payment_provider) return ''
      const amount = this.planPrimaryAmount(plan)
      if (!amount) return ''
      return formatCurrency(amount, this.currency, this.locale)
    },
  },
  async created() {
    await this.bootstrap()
  },
  methods: {
    ...mapActions('billing', [
      'loadBilling',
      'subscribe',
      'cancelSubscription',
      'fetchSubscription',
      'fetchSubscriptions',
      'verifyPakasirPayment',
      'resumePayment',
      'fetchSubscriptionDetail',
      'downloadInvoice',
    ]),

    async bootstrap() {
      this.paymentsError = false
      try {
        await this.loadBilling()
        await this.loadTransactions(this.transactionsPage)
      } catch {
        this.paymentsError = true
      }
      await this.checkQueryParams()
    },

    async retryLoad() {
      await this.bootstrap()
    },

    async loadTransactions(page = this.transactionsPage) {
      this.transactionsPage = page
      this.transactionsLoading = true
      this.paymentsError = false
      try {
        const params = {
          page: this.transactionsPage,
          limit: this.transactionsLimit,
        }
        if (this.transactionsCategory) params.category = this.transactionsCategory
        if (this.transactionsProvider) params.provider = this.transactionsProvider
        if (this.transactionsPlan) params.plan = this.transactionsPlan
        await this.fetchSubscriptions(params)
        if (this.transactionsPage > this.transactionsPageCount) {
          await this.loadTransactions(this.transactionsPageCount)
        }
      } catch {
        this.paymentsError = true
      } finally {
        this.transactionsLoading = false
      }
    },

    onTransactionsPageChange(page) {
      if (page < 1 || page > this.transactionsPageCount || page === this.transactionsPage) return
      this.loadTransactions(page)
    },

    onTransactionsLimitChange() {
      this.loadTransactions(1)
    },

    setTransactionCategory(value) {
      if (this.transactionsCategory === value) return
      this.transactionsCategory = value
      this.loadTransactions(1)
    },

    onTransactionFiltersChange() {
      this.loadTransactions(1)
    },

    isRecurringPlan(sub) {
      return !!sub?.plan?.features?.recurring
    },

    billingTypeLabel(sub) {
      if (this.isRecurringPlan(sub)) return this.$t('billing.tx_type_recurring')
      if (sub?.plan?.slug === 'free' || !sub?.payment_provider) return this.$t('billing.tx_type_free')
      return this.$t('billing.tx_type_one_time')
    },

    billingTypeClass(sub) {
      if (this.isRecurringPlan(sub)) return 'billing-tx-type--recurring'
      if (sub?.plan?.slug === 'free' || !sub?.payment_provider) return 'billing-tx-type--free'
      return 'billing-tx-type--once'
    },

    formatCount(n) {
      return Number(n).toLocaleString(this.locale === 'id' ? 'id-ID' : 'en-US')
    },

    planFeatureList(plan) {
      return buildPlanFeatureList(plan, this.$t.bind(this), this.planFeatureFlags)
    },

    canDownloadInvoice(sub) {
      if (!sub?.payment_provider || sub.status === 'pending') return false
      const slug = sub.plan?.slug
      return slug && slug !== 'free'
    },

    async openTransactionDetail(sub) {
      this.txDetail = { ...sub }
      this.txDetailLoading = true
      this.$modal.show('billing-tx-detail-modal')
      try {
        const detail = await this.fetchSubscriptionDetail(sub.id)
        this.txDetail = detail || sub
      } catch {
        this.$toast?.error?.(this.$t('billing.tx_detail_error'))
      } finally {
        this.txDetailLoading = false
      }
    },

    closeTransactionDetail() {
      this.$modal.hide('billing-tx-detail-modal')
    },

    onTxDetailClosed() {
      this.txDetail = null
      this.txDetailLoading = false
    },

    async onDownloadInvoice(sub) {
      if (!this.canDownloadInvoice(sub)) return
      this.invoiceDownloadingId = sub.id
      try {
        const blob = await this.downloadInvoice(sub.id)
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `invoice-${sub.id}.pdf`
        link.click()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        this.$toast?.error?.(e.response?.data?.message || this.$t('billing.tx_invoice_error'))
      } finally {
        this.invoiceDownloadingId = null
      }
    },

    formatDateShort(value) {
      if (!value) return ''
      const intlLocale = this.locale === 'id' ? 'id-ID' : 'en-US'
      return new Date(value).toLocaleDateString(intlLocale)
    },

    planPrimaryAmount(plan) {
      return planPrimaryAmount(plan, this.currency)
    },

    formatPrimaryPrice(plan) {
      const amount = this.planPrimaryAmount(plan)
      if (!amount) return this.$t('billing.free_price')
      return formatCurrency(amount, this.currency, this.locale)
    },

    secondaryPrice(plan) {
      const amount = planSecondaryAmount(plan, this.currency)
      if (!amount) return ''
      return formatCurrency(amount, planSecondaryCurrency(this.currency), this.locale)
    },

    async checkQueryParams() {
      const { success, cancelled, error, provider, order_id: orderId } = this.$route.query

      if (provider === 'pakasir' && orderId) {
        this.verifying = true
        try {
          await this.verifyPakasirPayment(orderId)
          this.alertMessage = this.$t('billing.local_payment_success')
          this.alertClass = 'alert-success'
          await this.loadTransactions()
        } catch (e) {
          const status = e.response?.status
          if (status === 202) {
            this.alertMessage = this.$t('billing.payment_pending')
            this.alertClass = 'alert-warning'
          } else {
            this.alertMessage = e.response?.data?.message || this.$t('billing.payment_failed')
            this.alertClass = 'alert-danger'
          }
        } finally {
          this.verifying = false
        }
        return
      }

      if (success) {
        this.alertMessage = this.$t('billing.subscription_activated')
        this.alertClass = 'alert-success'
        await Promise.all([this.fetchSubscription(), this.loadTransactions()])
      } else if (cancelled) {
        this.alertMessage = this.$t('billing.cancelled_checkout')
        this.alertClass = 'alert-warning'
      } else if (error) {
        this.alertMessage = this.$t('billing.subscription_error')
        this.alertClass = 'alert-danger'
      }
    },

    isCurrentPlan(plan) {
      if (this.subscription?.plan?.slug === plan.slug) return true
      if (!this.subscription && plan.slug === 'free') return true
      return this.currentPlanSlug === plan.slug
    },

    isSubscribeDisabled(plan) {
      if (this.subscribing || this.verifying || this.isCurrentPlan(plan)) return true
      if (plan.slug !== 'free' && !this.paymentReady) return true
      return false
    },

    planButtonLabel(plan) {
      if (this.isCurrentPlan(plan)) return this.$t('billing.current_plan')
      if (plan.slug === 'free') return this.$t('billing.switch_free')
      if (!this.paymentReady) return this.$t('billing.coming_soon')
      return this.$t('billing.switch_to', { plan: plan.name })
    },

    planActionClass(plan) {
      if (this.isCurrentPlan(plan) || this.isSubscribeDisabled(plan)) return 'btn-outline-secondary'
      if (plan.slug === 'free' || this.planPrimaryAmount(plan) === 0) return 'btn-outline-primary'
      return 'btn-primary'
    },

    formatDate(value) {
      const intlLocale = this.locale === 'id' ? 'id-ID' : 'en-US'
      return new Date(value).toLocaleDateString(intlLocale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    },

    isPayable(sub) {
      return sub?.status === 'pending'
    },

    providerLabel(provider) {
      const map = {
        paypal: this.$t('billing.provider_paypal'),
        pakasir: this.$t('billing.provider_local'),
      }
      return map[provider] || provider || '—'
    },

    statusLabel(status) {
      const map = {
        active: this.$t('billing.status_active'),
        pending: this.$t('billing.status_pending'),
        cancelled: this.$t('billing.status_cancelled'),
        suspended: this.$t('billing.status_suspended'),
        expired: this.$t('billing.status_expired'),
        trialing: this.$t('billing.status_trialing'),
      }
      return map[status] || status
    },

    statusTone(status) {
      const map = {
        active: 'billing-status--active',
        pending: 'billing-status--pending',
        cancelled: 'billing-status--cancelled',
        expired: 'billing-status--cancelled',
        suspended: 'billing-status--suspended',
        trialing: 'billing-status--trialing',
      }
      return map[status] || 'billing-status--cancelled'
    },

    async onSubscribe(plan) {
      if (plan.slug === 'free') {
        this.confirmSubscribe(null, plan)
        return
      }
      if (!this.paymentReady) return

      if (this.paypalReady && this.pakasirReady) {
        this.selectedPlan = plan
        this.$modal.show('profile-payment-modal')
        return
      }

      const provider = this.pakasirReady ? 'pakasir' : 'paypal'
      this.confirmSubscribe(provider, plan)
    },

    closePaymentModal() {
      this.$modal.hide('profile-payment-modal')
    },

    onPaymentOpened() {
      this.$nextTick(() => {
        const option = document.querySelector('.payment-modal .payment-option')
        if (option) option.focus()
      })
    },

    onPaymentClosed() {
      this.selectedPlan = null
    },

    async confirmSubscribe(provider, plan = this.selectedPlan) {
      if (!plan) return
      this.subscribing = true
      try {
        const result = await this.subscribe({ planSlug: plan.slug, provider })
        this.closePaymentModal()

        if (result.approval_url) {
          window.location.href = result.approval_url
          return
        }
        if (result.payment_url) {
          window.location.href = result.payment_url
          return
        }

        this.alertMessage = result.message || this.$t('billing.switched_to', { plan: plan.name })
        this.alertClass = 'alert-success'
        await Promise.all([this.fetchSubscription(), this.loadTransactions()])
      } catch (e) {
        this.$toast?.error?.(e.response?.data?.message || this.$t('billing.subscribe_error'))
      } finally {
        this.subscribing = false
      }
    },

    async onCancel() {
      const ok = await this.$confirm(this.$t('billing.cancel_confirm'), { danger: true })
      if (!ok) return
      this.cancelling = true
      try {
        await this.cancelSubscription()
        this.alertMessage = this.$t('billing.subscription_cancelled')
        this.alertClass = 'alert-info'
      } catch (e) {
        this.$toast?.error?.(e.response?.data?.message || this.$t('billing.subscribe_error'))
      } finally {
        this.cancelling = false
      }
    },

    async onPay(sub) {
      this.payingId = sub.id
      try {
        const result = await this.resumePayment(sub.id)
        const url = result?.payment_url || result?.approval_url
        if (url) {
          window.location.href = url
          return
        }
        this.$toast?.error?.(this.$t('billing.pay_resume_error'))
      } catch (e) {
        this.$toast?.error?.(e.response?.data?.message || this.$t('billing.pay_resume_error'))
      } finally {
        this.payingId = null
      }
    },
  },
}
</script>

<style scoped>
.profile-billing {
  color: var(--wc-text);
}

.billing-plan-card {
  margin-bottom: 2rem;
  border-radius: var(--wc-radius-xl);
  border: 1px solid var(--wc-border);
  background: var(--wc-bg-card);
  box-shadow: var(--wc-shadow-sm);
  overflow: hidden;
}

.billing-plan-card--free {
  --plan-card-accent: var(--wc-text);
}

.billing-plan-card--starter,
.billing-plan-card--pro {
  --plan-card-accent: var(--wc-primary);
}

.billing-plan-card--business {
  --plan-card-accent: var(--wc-primary-dark, var(--wc-primary));
}

.billing-plan-card__inner {
  padding: 1.35rem 1.5rem 1.5rem;
}

.billing-plan-card__header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem 1.25rem;
  margin-bottom: 1.25rem;
}

.billing-plan-card__identity {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  min-width: 0;
  flex: 1 1 14rem;
}

.billing-plan-card__intro {
  min-width: 0;
}

.billing-plan-card__eyebrow {
  margin: 0 0 0.2rem;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--wc-text);
  opacity: 0.72;
}

.billing-plan-card__title-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.45rem 0.6rem;
}

.billing-plan-card__name {
  margin: 0;
  font-size: clamp(1.25rem, 2.2vw, 1.625rem);
  font-weight: 700;
  color: var(--wc-text-heading);
  line-height: 1.15;
}

.billing-plan-card__price {
  margin: 0.35rem 0 0;
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--wc-primary);
}

.billing-plan-card__cancel {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  min-height: 40px;
  padding: 0.45rem 0.85rem;
  border: 1px solid rgba(220, 53, 69, 0.35);
  border-radius: var(--wc-radius-lg);
  background: #fff;
  color: #b42318;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.billing-plan-card__cancel:hover:not(:disabled) {
  background: #fef2f2;
  border-color: #dc3545;
}

.billing-plan-card__cancel:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.billing-plan-card__stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(9.5rem, 1fr));
  gap: 0.65rem;
  margin-bottom: 1rem;
}

.billing-plan-card__stat {
  padding: 0.75rem 0.85rem;
  border-radius: var(--wc-radius-lg);
  background: var(--wc-bg-subtle);
  border: 1px solid var(--wc-border);
}

.billing-plan-card__stat--highlight {
  background: rgba(var(--wc-primary-rgb), 0.08);
  border-color: rgba(var(--wc-primary-rgb), 0.2);
}

.billing-plan-card__stat-label {
  display: block;
  margin-bottom: 0.25rem;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--wc-text);
  opacity: 0.75;
}

.billing-plan-card__stat-value {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--wc-text-heading);
  line-height: 1.35;
}

.billing-plan-card__stat-value--lg {
  font-size: 0.9375rem;
}

.billing-plan-card__period {
  margin-bottom: 1rem;
}

.billing-plan-card__period-track {
  height: 8px;
  border-radius: 999px;
  background: var(--wc-bg-subtle);
  overflow: hidden;
}

.billing-plan-card__period-fill {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--plan-card-accent, var(--wc-primary));
  opacity: 0.85;
}

.billing-plan-card__period-caption {
  margin: 0.4rem 0 0;
  font-size: 0.75rem;
  color: var(--wc-text);
  opacity: 0.8;
}

.billing-plan-card__usage {
  padding: 0.85rem 1rem;
  border-radius: var(--wc-radius-lg);
  border: 1px dashed rgba(var(--wc-primary-rgb), 0.28);
  background: rgba(var(--wc-primary-rgb), 0.04);
}

.billing-plan-card__usage-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.35rem 0.75rem;
  margin-bottom: 0.5rem;
}

.billing-plan-card__usage-title {
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--wc-text-heading);
}

.billing-plan-card__usage-nums {
  font-size: 0.8125rem;
  font-variant-numeric: tabular-nums;
  color: var(--wc-text);
}

.billing-plan-card__usage-track {
  height: 6px;
  border-radius: 999px;
  background: rgba(var(--wc-primary-rgb), 0.12);
  overflow: hidden;
}

.billing-plan-card__usage-fill {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--wc-primary);
}

.billing-status {
  display: inline-flex;
  align-items: center;
  min-height: 1.5rem;
  padding: 0.15rem 0.5rem;
  border-radius: var(--wc-radius-sm);
  font-size: 0.75rem;
  font-weight: 600;
}

.billing-status--active {
  background: #d1f2ec;
  color: #155e52;
}

.billing-status--pending {
  background: #fff4d6;
  color: #92400e;
}

.billing-status--cancelled {
  background: #e9ecef;
  color: #495057;
}

.billing-status--suspended {
  background: #fdecea;
  color: #a12622;
}

.billing-status--trialing {
  background: #eaf2ff;
  color: #1d4ed8;
}

.billing-compare {
  margin-bottom: 2.5rem;
}

.billing-compare__title {
  margin: 0 0 1rem;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--wc-text-heading);
}

.billing-compare__soon,
.billing-compare__legal {
  margin: 1rem 0 0;
  font-size: 0.8125rem;
  color: var(--wc-text-muted, var(--wc-text));
}

.billing-plans {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.85rem;
}

.billing-plan {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.15rem 1.25rem;
  background: var(--wc-bg-card);
  border: 1px solid var(--wc-border);
  border-radius: var(--wc-radius-xl);
}

.billing-plan--current {
  border-color: var(--wc-primary);
  box-shadow: 0 0 0 1px rgba(var(--wc-primary-rgb), 0.12);
}

.billing-plan__top {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.billing-plan__name {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: var(--wc-text-heading);
}

.billing-plan__badge {
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--wc-primary);
}

.billing-plan__price-row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.35rem 0.5rem;
}

.billing-plan__price {
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--wc-text-heading);
  line-height: 1.1;
}

.billing-plan__interval,
.billing-plan__alt {
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--wc-text);
}

.billing-plan__features {
  list-style: none;
  margin: 0;
  padding: 0;
  flex: 1;
}

.billing-plan__features li {
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
  padding: 0.28rem 0;
  font-size: 0.8125rem;
  line-height: 1.4;
  color: var(--wc-text);
}

.billing-plan__features .fa-check {
  flex-shrink: 0;
  margin-top: 0.15rem;
  font-size: 0.7rem;
  color: var(--wc-success, #198754);
}

.billing-plan__action {
  margin-top: auto;
  min-height: 44px;
  width: 100%;
}

.billing-payments__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.billing-payments__title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--wc-text-heading);
}

.billing-payments__refresh {
  min-height: 44px;
}

.billing-payments__empty {
  color: var(--wc-text);
  margin: 0.75rem 0 0;
}

.billing-tx-toolbar {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.billing-tx-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.billing-tx-filters--secondary {
  gap: 0.5rem;
}

.billing-tx-select {
  min-width: 9rem;
  max-width: 12rem;
  min-height: 36px;
}

.billing-tx-type {
  display: inline-flex;
  align-items: center;
  padding: 0.12rem 0.45rem;
  border-radius: var(--wc-radius-sm);
  font-size: 0.75rem;
  font-weight: 600;
}

.billing-tx-type--recurring {
  color: #1d4ed8;
  background: #eaf2ff;
}

.billing-tx-type--once {
  color: var(--wc-text-heading);
  background: var(--wc-bg-subtle);
}

.billing-tx-type--free {
  color: var(--wc-text);
  background: transparent;
  padding-left: 0;
  padding-right: 0;
}

.billing-tx-table__plan {
  font-weight: 600;
  color: var(--wc-text-heading);
}

.billing-tx-table__period {
  font-size: 0.8125rem;
  white-space: nowrap;
}

.billing-tx-table-wrap {
  border: 1px solid var(--wc-border);
  border-radius: var(--wc-radius-lg);
}

.billing-tx-table {
  font-size: 0.875rem;
}

.billing-tx-table thead th {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--wc-text);
  border-bottom-width: 1px;
  white-space: nowrap;
}

.billing-tx-table__date {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.billing-tx-table__actions {
  width: 1%;
  white-space: nowrap;
  text-align: right;
}

.billing-tx-detail {
  padding: 1.5rem 1.75rem 1.75rem;
}

.billing-tx-detail__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.billing-tx-detail__title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--wc-text-heading);
}

.billing-tx-detail__close {
  border: 0;
  background: var(--wc-bg-subtle);
  color: var(--wc-text);
  width: 44px;
  height: 44px;
  border-radius: var(--wc-radius-lg);
  cursor: pointer;
  flex-shrink: 0;
}

.billing-tx-detail__meta {
  margin: 0 0 1.25rem;
}

.billing-tx-detail__row {
  display: grid;
  grid-template-columns: minmax(6rem, 38%) 1fr;
  gap: 0.5rem 1rem;
  padding: 0.45rem 0;
  border-bottom: 1px solid var(--wc-border);
  font-size: 0.875rem;
}

.billing-tx-detail__row dt {
  margin: 0;
  font-weight: 600;
  color: var(--wc-text);
}

.billing-tx-detail__row dd {
  margin: 0;
  color: var(--wc-text-heading);
}

.billing-tx-detail__code {
  font-size: 0.8125rem;
  word-break: break-all;
}

.billing-tx-detail__features-title {
  margin: 0 0 0.5rem;
  font-size: 0.9375rem;
  font-weight: 700;
}

.billing-tx-detail__features ul {
  margin: 0 0 1.25rem;
  padding-left: 1.15rem;
  font-size: 0.8125rem;
}

.billing-tx-detail__footer {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.5rem;
}

.billing-tx-table__dash {
  color: var(--wc-text);
  opacity: 0.45;
}

.billing-tx-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem 1rem;
  margin-top: 0.85rem;
}

.billing-tx-footer__meta {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--wc-text);
}

.billing-tx-footer__controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.65rem 0.85rem;
}

.billing-tx-footer__limit select {
  width: auto;
  min-width: 4.25rem;
  min-height: 36px;
}

.billing-tx-pagination .page-link {
  min-width: 2.25rem;
  text-align: center;
}

.payment-modal {
  padding: 1.75rem 2rem 2rem;
}

.payment-modal__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.payment-modal__title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--wc-text-heading);
}

.payment-modal__close {
  border: 0;
  background: var(--wc-bg-subtle);
  color: var(--wc-text);
  width: 44px;
  height: 44px;
  border-radius: var(--wc-radius-lg);
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
}

.payment-modal__close:hover:not(:disabled) {
  background: var(--wc-border);
  color: var(--wc-text-heading);
}

.payment-modal__plan {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 1rem 1.15rem;
  margin-bottom: 1.35rem;
  border-radius: var(--wc-radius-xl);
  background: var(--wc-primary-light);
  border: 1px solid rgba(var(--wc-primary-rgb), 0.18);
}

.payment-modal__plan-name {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--wc-text-heading);
}

.payment-modal__plan-price {
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--wc-primary);
}

.payment-modal__options {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.payment-modal__notice {
  margin: 1.25rem 0 0;
  font-size: 0.875rem;
  color: var(--wc-text);
  line-height: 1.5;
}

.payment-option {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 1rem;
  text-align: left;
  padding: 0.9rem 1.1rem;
  min-height: 56px;
  border-radius: var(--wc-radius-xl);
  border: 2px solid var(--wc-border);
  background: #fff;
  cursor: pointer;
}

.billing-plan .btn:focus-visible,
.billing-plan-card__cancel:focus-visible,
.billing-tx-table .btn:focus-visible,
.billing-tx-pagination .page-link:focus-visible,
.billing-payments__refresh:focus-visible,
.payment-option:focus-visible,
.payment-modal__close:focus-visible {
  outline: 2px solid var(--wc-primary);
  outline-offset: 2px;
}

.payment-option:hover:not(:disabled) {
  border-color: var(--wc-primary);
}

.payment-option:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.payment-option--pakasir:hover:not(:disabled) {
  border-color: var(--wc-success);
}

.payment-option__icon {
  width: 48px;
  height: 48px;
  border-radius: var(--wc-radius-lg);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  flex-shrink: 0;
}

.payment-option--paypal .payment-option__icon {
  background: var(--wc-primary-light);
  color: var(--wc-primary);
}

.payment-option--pakasir .payment-option__icon {
  background: #d1f2ec;
  color: #155e52;
}

.payment-option__body {
  flex: 1;
  min-width: 0;
}

.payment-option__title {
  display: block;
  font-size: 1rem;
  font-weight: 700;
  color: var(--wc-text-heading);
}

@media (min-width: 720px) {
  .billing-plans {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1100px) {
  .billing-plans {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .billing-plan-card__inner {
    padding: 1.15rem 1rem 1.25rem;
  }

  .billing-plan-card__cancel {
    width: 100%;
    justify-content: center;
  }

  .billing-plan-card__header {
    flex-direction: column;
  }

  .billing-tx-footer {
    flex-direction: column;
    align-items: stretch;
  }

  .billing-tx-footer__controls {
    justify-content: space-between;
  }

}

@media (max-width: 575px) {
  .payment-modal {
    padding: 1.25rem 1.15rem 1.5rem;
  }
}
</style>
